import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { PortalService } from "../../services/portal-service.service";
import { LogService } from "../../services/log.service";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Contracts } from "../../models/contracts";
import { ipValidator } from "../../validators/ipaddress";
import { signatureValidator } from "../../validators/signature";
import { Activations, ActivateLicense, ActivationResponse } from "../../models/activations";
import { MatDialog } from "@angular/material/dialog";
import { ContractSummaryComponent } from "../contract-summary/contract-summary.component";
import { CristieUtils } from "../../core/utils.service";

@Component({
  standalone: false,
  selector: "app-license-activation",
  templateUrl: "./license-activation.component.html",
  styleUrls: ["./license-activation.component.scss"],
})
export class LicenseActivationComponent implements OnInit {
  //  provides access to the model template in the component html.
  @ViewChild("activationModal") activationModalRef: TemplateRef<any>;
  ActivateLicenseForm: UntypedFormGroup;
  @Input() contract: Contracts;
  @ViewChild("contractSummary", { static: false })
  contractSummary: ContractSummaryComponent;
  combination: string;
  closeResult: string;
  success = false;
  error: any = null;
  activating = false;
  active = 1;
  sigHelp = "(Should be in the format xxxxxxxx-xxxxxxxx-xxxxxxxx-xxxxxxxx)";
  activationResponse = new Array<ActivationResponse>();
  expired = new Array<Activations>();
  siteActivation: Activations = null;

  constructor(
    private fb: UntypedFormBuilder,
    private portalService: PortalService,
    private dialog: MatDialog,
    private logService: LogService,
  ) {}

  ngOnInit() {
    this.active = 1;
    this.getActivations(this.contract.id);
    const siteLicensed = this.isSiteLicensed();
    this.ActivateLicenseForm = this.fb.group({
      agreement: 0,
      machineType: "Select",
      type2: "Select",
      secureSite: false,
      hostName: [siteLicensed ? "site licensed" : "", [Validators.required]],
      osType: "Select",
      signature: [siteLicensed ? "." : "", siteLicensed ? [] : [Validators.required, signatureValidator]],
      comment: [`Obtained by ${CristieUtils.decodeBase64(localStorage.getItem("name"))} VIA the Licensing Portal`, [Validators.required]],
      ipAddress: ["", [Validators.required, ipValidator]],
    });
    if (siteLicensed) {
      this.f.hostName.disable();
      this.f.signature.disable();
    }
  }

  getActivations(contractId: number) {
    return this.portalService.GetActivations(contractId, true).subscribe((all: Activations[]) => {
      const map = new Map<string, Activations>();
      this.siteActivation = null;
      // This removes duplicates from the activations list and takes the earliest timestamp (activation date) and uses the lastest expiry date
      all.forEach((a) => {
        if (a.siteLicence && this.isActivationRecord(a) && !this.siteActivation && !a.dateSurrendered) {
          this.siteActivation = a;
        }
        if (!this.isActivationRecord(a)) return; // token requests are not eligible for renewal
        const key = `${a.hostname}-${a.ipAddress}`;
        if (!map.has(key)) {
          map.set(key, a);
        } else {
          const existing = map.get(key)!;
          if (a.timestamp < existing.timestamp) {
            existing.timestamp = a.timestamp;
          }
          if (a.expiry && (!existing.expiry || a.expiry > existing.expiry)) {
            existing.expiry = a.expiry;
          }
        }
      });

      this.expired = Array.from(map.values()).filter((a) => a.expiry && a.expiry.getTime() < new Date().getTime());
    });
  }

  get f() {
    return this.ActivateLicenseForm.controls;
  }

  get signatureLength(): number {
    return String(this.f.signature?.value || "").length;
  }

  get showSignatureCountState(): boolean {
    return !!this.f.signature && (this.f.signature.dirty || this.f.signature.touched);
  }

  get signatureCountClass(): string {
    if (!this.showSignatureCountState) return "";
    return this.signatureLength === 35 ? "signature-count-valid" : "signature-count-invalid";
  }

  get siteLicenseExpiry(): Date | string {
    return this.siteActivation?.expiry || this.contract?.endDate;
  }

  open(content) {
    this.dialog.open(content, { width: "1200px", maxWidth: "95vw" });
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  checkChange() {
    if (this.isSiteLicensed()) {
      this.f.hostName.setValue("site licensed");
      this.f.signature.setValue(".");
      return;
    }
    if (this.f.secureSite.value) {
      this.f.hostName.setValue("REDACTED");
      this.f.hostName.disable();
    } else {
      this.f.hostName.setValue("");
      this.f.hostName.enable();
      this.f.secureSite.setValue(false);
    }
  }

  dropdownSelected() {
    if (this.f.machineType.value == "Select") return true;
    if (this.f.type2.value == "Select") return true;
    if (this.f.osType.value == "Select") return true;
    if (this.isInvalidPlatformCombination()) return true;
  }

  onMachineTypeChange(): void {
    if (this.isInvalidPlatformCombination() && this.f.osType.value === "AIX") {
      this.f.osType.setValue("Select");
    }
    this.checkCombination();
  }

  isInvalidPlatformCombination(): boolean {
    return this.f.osType?.value === "AIX" && (this.f.machineType?.value === "Virtual" || this.f.type2?.value === "Desktop");
  }

  checkCombination() {
    if (this.f.type2.value === "Server") {
      this.combination = this.f.machineType.value + " " + this.f.type2.value;
    }

    if (this.f.type2.value === "Desktop") {
      this.combination = this.f.type2.value;
    }

    if (this.isSiteLicensed()) {
      return false;
    }

    if (!this.contract.activations) {
      return true;
    }

    switch (this.combination) {
      case "Physical Server": {
        if (this.contract.activations.available > 0) {
          return false;
        }
        return true;
      }

      case "Virtual Server": {
        if (this.contract.vmActivations.available > 0) {
          return false;
        }
        return true;
      }

      case "Desktop": {
        if (this.contract.desktopActivations.available > 0) {
          return false;
        }
        return true;
      }
    }
  }

  activateLicense() {
    this.resetMessage();
    if (this.activating) {
      return false;
    }
    if (this.ActivateLicenseForm.invalid || this.contract == null || this.isInvalidPlatformCombination()) {
      return false;
    }
    this.activating = true;
    const act = new ActivateLicense();
    const contract = this.contract.id;
    act.machineType = this.f.machineType.value.toUpperCase();
    act.product = this.contract.product.toUpperCase();
    act.platform = this.f.osType.value.toUpperCase();
    act.signature = this.f.signature.value;
    act.attribute = this.f.type2.value.toUpperCase();
    act.comment = this.f.comment.value;
    act.ip = this.f.ipAddress.value;
    act.hostname = this.f.hostName.value;

    return this.portalService.ActivateLicense(Array(act), contract).subscribe(
      (response) => {
        this.success = true;
        this.activationResponse = response;
        this.decrementAvailableActivations();
        this.activating = false;
        this.open(this.activationModalRef);
      },
      (error) => {
        this.logService.log("Activation failed: ", error);
        this.error = error;
        this.activating = false;
      },
    );
  }

  copyActivationDetails() {
    const entries = this.activationResponse || [];

    if (!entries.length) {
      return;
    }

    const lines = [
      "HOSTNAME\tKEY\tCOMMAND",
      ...entries.map((e) => `${e.hostname || ""}\t${e.code || ""}\t${this.getLicenseCommand(e)}`),
    ];

    const textToCopy = lines.join("\n");
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => console.log("Copied activation details!"))
      .catch((err) => console.error("Failed to copy activation details", err));
  }

  getLicenseCommand(entry: ActivationResponse): string {
    return this.getPlatformLicenseCommand(entry?.code || "", this.f.osType?.value || "Windows");
  }

  getSiteLicenseCommand(platform: string): string {
    return this.getPlatformLicenseCommand(this.siteActivation?.code || "", platform);
  }

  private getPlatformLicenseCommand(key: string, platform: string): string {
    const product = String(this.contract?.product || "").toUpperCase();
    if (String(platform || "").toUpperCase().includes("WINDOWS")) {
      return `licmgr.exe /p ${product} /act ${key}`;
    }

    return `licmgr -p ${product} --act ${key}`;
  }

  copyLicenseCommand(entry: ActivationResponse): void {
    navigator.clipboard.writeText(this.getLicenseCommand(entry));
  }

  copySiteLicenseCommand(platform: string): void {
    navigator.clipboard.writeText(this.getSiteLicenseCommand(platform));
  }

  copySiteLicenseKey(): void {
    navigator.clipboard.writeText(this.siteActivation?.code || "");
  }

  private isWindowsActivation(): boolean {
    return String(this.f.osType?.value || "").toUpperCase().includes("WINDOWS");
  }

  resetMessage() {
    this.success = false;
    this.error = null;
  }

  activateDisabled(): boolean {
    return this.activating || this.ActivateLicenseForm.invalid || this.checkCombination() || this.dropdownSelected();
  }

  isSiteLicensed(): boolean {
    return this.contract != null && this.contract.siteEnabled === true;
  }

  private isActivationRecord(activation: Activations): boolean {
    return activation.activationType === "ACTIVATION" || activation.activationType === "RENTAL";
  }

  canSelectActivationType(type: "Physical" | "Virtual" | "Desktop"): boolean {
    if (this.isSiteLicensed()) {
      return Number(this.contract.siteLicences || 0) > 0;
    }

    switch (type) {
      case "Physical":
        return Number(this.contract.activations?.available || 0) > 0;
      case "Virtual":
        return Number(this.contract.vmActivations?.available || 0) > 0;
      case "Desktop":
        return Number(this.contract.desktopActivations?.available || 0) > 0;
    }
  }

  private decrementAvailableActivations(): void {
    if (this.isSiteLicensed()) {
      this.contract.siteLicences = Math.max(0, Number(this.contract.siteLicences || 0) - 1);
      return;
    }

    switch (this.combination) {
      case "Physical Server":
        if (this.contract.activations) {
          this.contract.activations.available = Math.max(0, Number(this.contract.activations.available || 0) - 1);
        }
        break;
      case "Virtual Server":
        if (this.contract.vmActivations) {
          this.contract.vmActivations.available = Math.max(0, Number(this.contract.vmActivations.available || 0) - 1);
        }
        break;
      case "Desktop":
        if (this.contract.desktopActivations) {
          this.contract.desktopActivations.available = Math.max(0, Number(this.contract.desktopActivations.available || 0) - 1);
        }
        break;
    }
  }
}
